/**
 * Concept-duplaat-identiteit. Twee concepten zijn "klonen" wanneer
 * **type + naam + stereotypes** identiek zijn (besluit 2026-06-19). Gebruikt
 * door de rechtermuis-"Samenvoegen" én de duplicaat-detectie op create/rename.
 *
 *  - **Naam**: bilinguaal, trim + case-insensitive; lankmoedig zoals relaties —
 *    geen conflict per taal + beide-genoemd-of-beide-leeg, zodat een en-only
 *    naam matcht met een bilinguale.
 *  - **Stereotypes**: ongeordende set-gelijkheid.
 *
 * De duplicaat-VRAAG is named-only (zie `conceptHasName`); een naamloos concept
 * triggert niets. De rechtermuis-merge gebruikt enkel `isSameConceptClone`.
 *
 * Junctions doen niet mee — zie `isJunction`.
 */
import type { Concept } from "@ae/domain";

type ConceptIdentityShape = Pick<Concept, "typeId" | "name" | "stereotypes">;

const norm = (s: string | undefined): string => (s ?? "").trim().toLowerCase();

/**
 * Een junction is een PUNT in een stroom, geen object met identiteit.
 *
 * Ze dragen allemaal de naam van hun palet-tegel ("Samenvoeging", "Vertakking
 * XOR"), dus zonder deze regel is elke tweede er een "kloon" van de eerste en
 * krijg je bij het neerzetten de samenvoeg-vraag — op een nagetekend diagram
 * van Sharp met dertig samenvoegpunten dus dertig keer, met een lijst van
 * dertig gelijkluidende regels (gemeld 2026-09-19). Twee vertakkingen op
 * verschillende plekken in een proces zijn verschillende punten, hoe gelijk ze
 * er ook uitzien. Zelfde redenering als de `drawing.`-uitzondering hieronder.
 */
const JUNCTION_TYPES = ["archimate.AndJunction", "archimate.OrJunction"];
const isJunction = (typeId: string): boolean => JUNCTION_TYPES.includes(typeId);

export function isSameConceptClone(a: ConceptIdentityShape, b: ConceptIdentityShape): boolean {
  if (a.typeId !== b.typeId) return false;
  if (isJunction(a.typeId)) return false;
  const anl = norm(a.name?.nl);
  const bnl = norm(b.name?.nl);
  const aen = norm(a.name?.en);
  const ben = norm(b.name?.en);
  if (anl && bnl && anl !== bnl) return false; // nl-conflict
  if (aen && ben && aen !== ben) return false; // en-conflict
  if (!!(anl || aen) !== !!(bnl || ben)) return false; // één genoemd, één leeg
  const sa = a.stereotypes ?? [];
  const sb = b.stereotypes ?? [];
  if (sa.length !== sb.length) return false;
  const set = new Set(sa);
  return sb.every((s) => set.has(s));
}

/** Heeft het concept een (niet-lege) naam? De duplicaat-vraag is named-only. */
export function conceptHasName(c: Pick<Concept, "name">): boolean {
  return !!(norm(c.name?.nl) || norm(c.name?.en));
}

/** Live (niet-tombstoned, niet-drawing) concepten die klonen zijn van `concept`.
 *  Sluit een optionele `excludeId` uit (het concept zelf bij een check). */
export function findDuplicateConcepts(
  concept: ConceptIdentityShape,
  all: Concept[],
  excludeId?: string,
): Concept[] {
  return all.filter(
    (o) =>
      o.id !== excludeId &&
      !o.tombstoneInBranch &&
      !o.typeId.startsWith("drawing.") &&
      isSameConceptClone(concept, o),
  );
}
